import { inject, Injectable } from '@angular/core';
import { DeepLinkParametersEnum, HostDataReaderService, QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { LoggerService } from '@lx/shared/logger';
import { SessionStorageService } from '@lx/shared/storage';
import { getDateFromString, getDateStringFromDate, isEmptyObj, normalizeString, QuoteToolPreselectedModes } from '@lx/shared/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { z } from 'zod';
import { AcademicYearAbroadDeepLinksParametersSchema } from '../../../schemas';
import { QuoteToolLiteState, SharedDataStore } from '../../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { AcademicYearAbroadDeepLinkParameters, AyaDuration, GroupedAyaCourse, RouteIds } from '../../../types';
import { findSchoolLocationByCode } from '../../../utils';
import { AyaCourseService } from '../../aya-course.service';
import { AyaLocationService } from '../../aya-location.service';
import { AyaScheduleService } from '../../aya-schedule.service';
import { StepDefinitionService } from '../../step-definition.service';
import { Flows, getQuoteToolStorageKey, SchoolLocation } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class AcademicYearAbroadDeepLinkStrategyService implements QuoteToolStrategyService<RouteIds> {
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly stepDefinitionService = inject(StepDefinitionService);
  private readonly hostDataReader = inject(HostDataReaderService);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly ayaCourseService = inject(AyaCourseService);
  private readonly ayaLocationService = inject(AyaLocationService);
  private readonly logger = inject(LoggerService);
  private readonly ayaScheduleService = inject(AyaScheduleService);
  private readonly storageKey = getQuoteToolStorageKey();

  getStepToNavigate(): Observable<RouteIds> | RouteIds {
    return of(RouteIds.course);
  }

  loadData$(): Observable<boolean> {
    const deepLinkParameters = this.getDeepLinkParameters();
    const parsedParameters = this.parseDeepLinkParameters(deepLinkParameters);

    if (!parsedParameters.success) {
      this.logger.error('Invalid deep link parameters', z.prettifyError(parsedParameters.error));
      return of(false);
    }

    const { languageCode, courseCode, schoolCode, duration, startDate, deeplinkType } = parsedParameters.data;

    // If no courseCode or languageCode provided, skip location fetching (normal AYA flow without deep link)
    const ayaLocations$ = courseCode && languageCode ? this.ayaLocationService.getAyaLocations(courseCode, languageCode).pipe(take(1)) : of([]);

    // If no languageCode provided, skip course fetching (normal AYA flow without deep link)
    const ayaCourses$ = languageCode ? this.ayaCourseService.getGroupedCourses(languageCode).pipe(take(1)) : of([]);

    return combineLatest([this.sharedDataStore.languages$.pipe(take(1)), ayaCourses$, ayaLocations$]).pipe(
      switchMap(([languages, ayaCourses, ayaLocations]) => {
        const selectedLanguage = languages.find((l) => normalizeString(l.code) === normalizeString(languageCode));
        const selectedCourse = this.findCourseInGroups(ayaCourses, courseCode);
        const selectedSchool = findSchoolLocationByCode(ayaLocations, schoolCode);

        // Log error only if parameters were provided but entities not found
        if (languageCode && !selectedLanguage) {
          this.logger.error('Language not found', languageCode);
          return of(undefined);
        }

        // Only check if course was found if we attempted to search for it (i.e., courseCode was provided)
        if (courseCode && !selectedCourse) {
          this.logger.error('Course not found', courseCode);
          return of(undefined);
        }

        // Only check if school was found if we attempted to search for it (i.e., schoolCode was provided)
        if (schoolCode && !selectedSchool) {
          this.logger.error('School not found', schoolCode);
          return of(undefined);
        }

        // All deep link parameters are required except startDate
        // If any required parameter is missing, return undefined to prevent incomplete state
        if (!languageCode || !schoolCode || !courseCode || !duration || !selectedLanguage || !selectedSchool || !selectedCourse) {
          this.logger.warn('Incomplete deep link parameters - all fields except startDate are required');
          return of(undefined);
        }

        return this.ayaScheduleService.getDurationOptions(courseCode, schoolCode).pipe(
          switchMap((durationOptions) => {
            const matchingDurationOption = durationOptions.find((option) => option.value.weeks === duration);
            if (!matchingDurationOption) {
              this.logger.error('Duration option not found', String(duration));
              return of(undefined);
            }
            return this.getValidatedStartDate(selectedCourse.code, schoolCode, matchingDurationOption.value, startDate).pipe(
              switchMap((validatedStartDate) => {
                if (validatedStartDate === undefined) {
                  this.logger.error('No start dates available for the selected course and duration');
                  return of(undefined);
                }
                return of(
                  this.prepareAyaStoreData(languageCode, selectedSchool, selectedCourse, matchingDurationOption.value, validatedStartDate, deeplinkType),
                );
              }),
            );
          }),
        );
      }),
      tap((ayaData: Partial<QuoteToolLiteState>) => {
        if (!isEmptyObj(ayaData)) {
          this.sessionStorageService.set(this.storageKey, { ...ayaData });
        }
      }),
      map((selectedData: Partial<QuoteToolLiteState>) => !isEmptyObj(selectedData)),
      catchError((error) => {
        this.logger.error('Error during loadData$', error);
        return of(false);
      }),
    );
  }

  private getDeepLinkParameters(): AcademicYearAbroadDeepLinkParameters {
    const queryParams = this.hostDataReader.getQueryParams();
    const quoteToolModeQueryParam = this.hostDataReader.getQuoteToolMode();
    const durationParam = queryParams.get(DeepLinkParametersEnum.duration);
    const duration = durationParam ? Number(durationParam) : undefined;

    return {
      preselectedMode: quoteToolModeQueryParam ? (quoteToolModeQueryParam as QuoteToolPreselectedModes) : undefined,
      languageCode: queryParams.get(DeepLinkParametersEnum.languageCode) || undefined,
      courseCode: queryParams.get(DeepLinkParametersEnum.courseCode) || undefined,
      schoolCode: queryParams.get(DeepLinkParametersEnum.schoolCode) || undefined,
      duration: duration !== undefined && !Number.isNaN(duration) ? duration : undefined,
      startDate: queryParams.get(DeepLinkParametersEnum.startDate) || undefined,
      deeplinkType: queryParams.get(DeepLinkParametersEnum.deeplinkType) || undefined,
    };
  }

  private parseDeepLinkParameters(deepLinkParameters: AcademicYearAbroadDeepLinkParameters) {
    return AcademicYearAbroadDeepLinksParametersSchema.safeParse(deepLinkParameters);
  }

  private findCourseInGroups(ayaCourses: GroupedAyaCourse[], courseCode: string): CourseDetails | undefined {
    for (const group of ayaCourses) {
      const course = group.items.find((item) => normalizeString(item.value) === normalizeString(courseCode));
      if (course) {
        return {
          type: group.value,
          code: course.value,
          name: course.label,
        };
      }
    }
    return undefined;
  }

  private prepareAyaStoreData(
    languageCode?: string,
    school?: SchoolLocation,
    course?: CourseDetails,
    duration?: AyaDuration,
    startDate?: Date,
    deeplinkType?: string,
  ): Partial<QuoteToolLiteState & { currentStepId: string }> {
    return {
      currentStepId: this.stepDefinitionService.getStepDefinition(RouteIds.course, Flows.Aya)?.id,
      activeFlow: Flows.Aya,
      ...(languageCode && { selectedLanguage: languageCode }),
      ...(school && { selectedSchoolLocation: school }),
      ...(course && { selectedCourse: course }),
      ...(duration && { selectedAyaDuration: duration }),
      ...(startDate && { selectedStartDate: getDateStringFromDate(startDate) }),
      deeplinkType,
    };
  }

  /**
   * Validates the start date for AYA courses by checking against available start dates.
   * If the provided start date doesn't match any available date or is undefined, falls back to the first available date.
   * If no start dates are available from the backend, returns undefined (business rules are handled on backend).
   */
  private getValidatedStartDate(courseCode: string, schoolCode: string, duration: AyaDuration, startDate: Date | undefined): Observable<Date | undefined> {
    return this.ayaScheduleService.getStartDates(courseCode, schoolCode, duration.months).pipe(
      map((startDates) => {
        if (startDates.length === 0) {
          this.logger.warn('No start dates available for the selected course and duration');
          return undefined;
        }

        const startDateString = getDateStringFromDate(startDate);
        const validStartDate = startDates.find((date) => date.date === startDateString);

        if (validStartDate) {
          return startDate;
        }

        this.logger.warn('Invalid or missing start date for the selected course, taking the first available one');
        return getDateFromString(startDates[0].date);
      }),
    );
  }
}
